import { CartLinesUpdateEvent, StandardEvents } from '@shopify/events';

const PROPERTY = '_vela_cart_addon';
const QUANTITY = '_vela_cart_addon_quantity';
const PREFERENCE = '_vela_addon_opt_out';
// Asset URLs can differ after an editor refresh. Every copy on this page must
// share one queue and one selection, even if the browser evaluates both URLs.
const runtime = globalThis[Symbol.for('vela.cart-addon.runtime.v1')] ??= {
  queue: Promise.resolve(),
  pending: 0,
  state: { cart: null, busy: false, error: '', selection: null },
  selectionRevision: 0,
  initialized: false,
};
const state = runtime.state;
runtime.keyChanges ??= new Map();
runtime.cartRevision ??= 0;
runtime.cartEvents ??= new Set();
runtime.publishedEvents ??= new WeakSet();
const STALE_CART = 'Your cart changed. Please check the updated items and try again.';

function propertiesObject(properties) {
  return Array.isArray(properties)
    ? Object.fromEntries(properties.filter((entry) => Array.isArray(entry) && entry.length === 2))
    : (properties && typeof properties === 'object' ? properties : {});
}

function sameProperties(a, b) {
  const entries = (value) => Object.entries(propertiesObject(value)).sort(([x], [y]) => x.localeCompare(y));
  return JSON.stringify(entries(a)) === JSON.stringify(entries(b));
}

// Resolve only a key transition confirmed by this page's own successful write.
// A different line with the same variant is not proof that it is the same item.
function currentKey(key) {
  const visited = new Set();
  while (!state.cart?.items.some((item) => item.key === key) && runtime.keyChanges.has(key) && !visited.has(key)) {
    visited.add(key);
    key = runtime.keyChanges.get(key);
  }
  return key;
}

function changeDetails(url, options, metadata) {
  if (!/\/change(?:\.js)?(?:\?|$)/.test(String(url))) return null;
  let body;
  try { body = JSON.parse(options.body); } catch { return null; }
  if (typeof body.id !== 'string' || !Number.isSafeInteger(body.quantity) || body.quantity < 0) return null;
  const cached = state.cart?.items.find((item) => item.key === currentKey(body.id));
  const provided = propertiesObject(metadata?.properties);
  return {
    key: body.id,
    quantity: body.quantity,
    variantId: metadata?.variantId || cached?.variant_id,
    properties: provided[PROPERTY] === 'true' ? provided : propertiesObject(cached?.properties ?? provided),
  };
}

function configuration() {
  const element = document.getElementById('vela-cart-addon-config');
  return element ? JSON.parse(element.textContent) : null;
}

function notify() {
  document.documentElement.toggleAttribute('data-vela-addon-busy', runtime.pending > 0);
  state.busy = runtime.pending > 0;
  document.dispatchEvent(new CustomEvent('vela:cart-addon-state', { detail: state }));
}

function enqueue(operation) {
  runtime.pending += 1;
  notify();
  const result = runtime.queue.then(operation);
  runtime.queue = result.catch(() => {}).finally(() => {
    runtime.pending -= 1;
    notify();
  });
  return result;
}

function sectionIds() {
  return [...new Set([...document.querySelectorAll('cart-items-component[data-section-id]')]
    .map((element) => element.dataset.sectionId))].slice(0, 5);
}

function cartURL(action = '') {
  return `${window.Shopify?.routes?.root || '/'}cart${action ? '/' + action : ''}.js`;
}

async function request(action, payload) {
  const response = await fetch(cartURL(action), payload ? {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify({ ...payload, sections: sectionIds(), sections_url: window.location.pathname }),
  } : { headers: { Accept: 'application/json' }, cache: 'no-store' });
  const data = await response.json();
  if (!response.ok || data.status || data.errors) {
    throw new Error(data.description || data.message || String(data.errors || 'Unable to update cart.'));
  }
  return data;
}

async function renderSections(cart) {
  // Cart mutations already return these sections. Only repair missing/null HTML.
  const ids = sectionIds().filter((id) => typeof cart.sections?.[id] !== 'string' || !cart.sections[id].trim());
  if (!ids.length) return cart;
  const url = new URL(window.location.href);
  url.searchParams.set('sections', ids.join(','));
  const response = await fetch(url, { cache: 'no-store' });
  if (!response.ok) throw new Error('Unable to refresh cart. Please refresh the page.');
  cart.sections = { ...cart.sections, ...await response.json() };
  if (ids.some((id) => typeof cart.sections[id] !== 'string' || !cart.sections[id].trim())) {
    throw new Error('Unable to refresh cart. Please refresh the page.');
  }
  return cart;
}

function isCart(data) {
  return Array.isArray(data?.items) && Number.isFinite(data.item_count)
    && data.attributes != null && typeof data.attributes === 'object' && !Array.isArray(data.attributes)
    && typeof data.token === 'string' && typeof data.currency === 'string' && /^[A-Z]{3}$/.test(data.currency)
    && Number.isFinite(data.total_price)
    && data.items.every((item) => typeof item.key === 'string' && item.id != null && item.variant_id != null
      && Number.isFinite(item.quantity) && Number.isFinite(item.final_line_price));
}

function initialCart() {
  // This JSON already accompanies the rendered cart. Reuse it on a normal
  // navigation instead of competing with the first-screen images for a GET.
  for (const element of document.querySelectorAll('cart-items-component > script[data-vela-cart-state]')) {
    try {
      const data = JSON.parse(element.textContent);
      if (!Array.isArray(data?.items)) continue;
      if (data.token != null && typeof data.token !== 'string') continue;
      if (!data.items.every((item) => {
        const properties = item?.properties;
        return properties == null || (Array.isArray(properties)
          ? properties.every((entry) => Array.isArray(entry) && entry.length === 2 && typeof entry[0] === 'string')
          : typeof properties === 'object');
      })) continue;
      const cart = {
        ...data,
        currency: typeof data.currency === 'string' ? data.currency : data.currency?.iso_code,
        items: data.items.map((item) => ({ ...item, properties: propertiesObject(item.properties) })),
      };
      // Liquid may omit the Ajax-only token. It is not needed for the local
      // add-on plan; keep the real snapshot tokenless rather than inventing one.
      if (!isCart({ ...cart, token: '' })) continue;
      if (!cart.items.every((item) => item.key && Number.isSafeInteger(item.quantity) && item.quantity > 0)) continue;
      if (new Set(cart.items.map((item) => item.key)).size !== cart.items.length) continue;
      if (cart.item_count !== cart.items.reduce((count, item) => count + item.quantity, 0)) continue;
      return cart;
    } catch { /* Missing or invalid server data keeps the existing network fallback. */ }
  }
  return null;
}

function initialize() {
  // A second asset URL can be evaluated after the editor enables the feature.
  // Do not replace an already active runtime with the earlier page snapshot.
  if (runtime.pending || state.cart) {
    void synchronize();
    return;
  }
  const config = configuration();
  const cart = initialCart();
  if (config && cart) {
    const plan = addonPlan(cart, config);
    if (!plan.add && !plan.clearPreference && !Object.keys(plan.updates).length) {
      state.cart = cart;
      notify();
      return;
    }
  }
  // Never repair from an old HTML snapshot. Read Shopify's current cart before
  // any automatic add/removal, as well as when no usable snapshot was rendered.
  void synchronize();
}

async function responseCart(data) {
  // change/update already return a full cart. add returns only the added lines,
  // so read the post-mutation cart rendered into its bundled sections instead.
  if (isCart(data)) return data;
  const template = document.createElement('template');
  for (const html of Object.values(data.sections || {})) {
    if (typeof html !== 'string' || !html.includes('data-vela-cart-state')) continue;
    template.innerHTML = html;
    const snapshot = template.content.querySelector('cart-items-component > script[data-vela-cart-state]');
    try {
      const cart = JSON.parse(snapshot?.textContent || 'null');
      if (isCart(cart)) return { ...cart, sections: data.sections };
    } catch { /* Fall back to the authoritative Cart API if a section is malformed. */ }
  }
  // A later read may include another tab's changes; regenerate its HTML too.
  return await request('');
}

// Only lines carrying our private property are managed; ordinary purchases are preserved.
function selectedQuantity(item) {
  const quantity = Number(item?.properties?.[QUANTITY]);
  return Number.isSafeInteger(quantity) && quantity > 0 ? quantity : null;
}

export function addonPlan(cart, config) {
  const managed = cart.items.filter((item) => item.properties?.[PROPERTY] === 'true');
  const regular = cart.items.filter((item) => item.properties?.[PROPERTY] !== 'true');
  const optedOut = String(cart.attributes?.[PREFERENCE] || '') === String(config.variantId);
  const wanted = regular.length > 0 && !optedOut && config.available;
  const matching = managed.filter((item) => String(item.variant_id) === String(config.variantId));
  // An explicit customer quantity takes precedence over a concurrent auto-add.
  const keep = wanted ? matching.find((item) => selectedQuantity(item) !== null) || matching[0] : null;
  const updates = {};
  for (const item of managed) {
    // A manual edit changes the line's properties/key, so an automatic add can
    // only create a separate line. Preserve Shopify's accepted manual quantity.
    const quantity = item === keep ? (selectedQuantity(item) !== null ? item.quantity : 1) : 0;
    if (item.quantity !== quantity) updates[item.key] = quantity;
  }
  return {
    updates,
    add: wanted && !keep,
    clearPreference: regular.length === 0 && Boolean(cart.attributes?.[PREFERENCE]),
  };
}

async function normalizeQuantities(cart, config) {
  const plan = addonPlan(cart, config);
  if (Object.keys(plan.updates).length || plan.clearPreference) {
    cart = await request('update', {
      updates: plan.updates,
      ...(plan.clearPreference ? { attributes: { [PREFERENCE]: '' } } : {}),
    });
  }
  return cart;
}

async function reconcile(cart, config) {
  cart = await normalizeQuantities(cart, config);
  if (addonPlan(cart, config).add) {
    const added = await request('add', {
      items: [{ id: Number(config.variantId), quantity: 1, properties: { [PROPERTY]: 'true' } }],
    });
    // Shopify add increments an existing line. Another tab can add the same
    // add-on after our earlier snapshot, so normalize the actual response
    // before publishing totals. The normal one-item path sends no extra request.
    cart = await normalizeQuantities(await responseCart(added), config);
  }
  state.cart = cart;
  state.error = '';
  return cart;
}

async function recover(error) {
  state.error = error.message;
  // A network failure can occur after Shopify has committed the change.
  // Read the authoritative state before showing a checked/unchecked control.
  try {
    state.cart = await request('');
    return await renderSections(state.cart);
  } catch {
    // Never replace a successful primary mutation with a stale cached response.
    return null;
  }
}

function publish(cart, { rememberCount = false } = {}) {
  // A tokenless initial Liquid snapshot is local state, not an Ajax cart event.
  // If recovery is offline, leave the last rendered cart and show the error.
  if (!isCart(cart)) return;
  runtime.latestCartCount = cart.item_count;
  // The icon module can register after a fast history refresh. Its existing
  // connectedCallback reads this recent-count cache, so it must receive the
  // same authoritative count even if it missed the update event.
  if (rememberCount) {
    try {
      sessionStorage.setItem('cart-count', JSON.stringify({ value: String(cart.item_count), timestamp: Date.now() }));
    } catch { /* Storage restrictions must never block cart updates or checkout. */ }
    if (!customElements.get('cart-icon') && document.querySelector('cart-icon') && !runtime.iconRecoveryScheduled) {
      runtime.iconRecoveryScheduled = true;
      // Do not hold the cart queue while a low-priority icon module loads.
      // Native writes with the add-on disabled also update this event count.
      void customElements.whenDefined('cart-icon').then(() => {
        renderRecoveredIconCount();
      }).catch(() => {});
    }
  }
  const event = new CartLinesUpdateEvent({
    action: 'update', context: 'cart', lines: [],
    promise: Promise.resolve({
      cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
      detail: { source: 'vela-cart-addon', items: cart.items, sections: cart.sections, itemCount: cart.item_count },
    }),
  });
  runtime.publishedEvents.add(event);
  document.dispatchEvent(event);
}

function renderRecoveredIconCount() {
  if (!Number.isSafeInteger(runtime.latestCartCount)) return;
  for (const icon of document.querySelectorAll('cart-icon')) {
    void Promise.resolve(icon.renderCartBubble?.(runtime.latestCartCount, false)).catch(() => {});
  }
}

/** Restore the displayed cart after history navigation, with or without an add-on. */
function restoreCartView() {
  if (runtime.returnRefresh) return runtime.returnRefresh;
  runtime.returnRefresh = (async () => {
    // This module is a dependency of cart-items-component. Waiting outside the
    // cart queue lets that module finish registering, and never blocks checkout
    // if its download fails. Read fresh data only after its listeners are ready.
    if (document.querySelector('cart-items-component') && !customElements.get('cart-items-component')) {
      await customElements.whenDefined('cart-items-component');
    }
    if (!sectionIds().length && !document.querySelector('cart-icon')) return;
    let superseded;
    do {
      // Native theme writes dispatch their event before starting the request.
      // Never wait for those promises inside the queue: their write may itself
      // be queued behind this restoration.
      while (runtime.cartEvents.size) await Promise.all([...runtime.cartEvents]);
      superseded = false;
      await enqueue(async () => {
        const revision = runtime.cartRevision;
        const isCurrent = () => revision === runtime.cartRevision && !runtime.cartEvents.size;
        if (!isCurrent()) { superseded = true; return; }
        for (let attempt = 0; attempt < 2; attempt += 1) {
          try {
            let cart = await request('');
            if (!isCurrent()) { superseded = true; return; }
            const config = configuration();
            if (config) cart = await reconcile(cart, config);
            if (!isCurrent()) { superseded = true; return; }
            cart = await renderSections(cart);
            if (!isCurrent()) { superseded = true; return; }
            state.cart = cart;
            state.error = '';
            publish(cart, { rememberCount: true });
            return;
          } catch (error) {
            state.error = error.message;
            if (!isCurrent()) { superseded = true; return; }
            // One fresh retry handles a failed read/null section. If it also
            // fails, preserve the visible cart; never publish an empty fallback.
          }
        }
      });
    } while (superseded);
  })().catch(() => {}).finally(() => { runtime.returnRefresh = null; });
  return runtime.returnRefresh;
}

/** Preserve customer line edits without treating an automatic duplicate as a quantity choice. */
function lineChangeRequest(url, options, change, config, queuedCart) {
  const body = JSON.parse(options.body);
  // The rendered row may be newer than our cached cart (for example after a
  // discount). Only remap its key if another queued operation just updated state.
  const current = state.cart !== queuedCart ? state.cart?.items.find((item) => item.key === currentKey(change.key)) : null;
  if (state.cart !== queuedCart && state.cart && !current) throw new Error(STALE_CART);
  const key = current?.key || change.key;
  if (change.quantity === 0) {
    // update.js may silently ignore an expired key with quantity zero. change.js
    // must confirm the exact line was removed before we save the opt-out choice.
    return { url, options: { ...options, body: JSON.stringify({ ...body, id: key, quantity: 0 }) } };
  }
  return { url, options: { ...options, body: JSON.stringify({
    ...body,
    id: key,
    quantity: change.quantity,
    // change.js replaces all properties, so retain the existing values.
    properties: { ...(current?.properties || change.properties), [QUANTITY]: String(change.quantity) },
  }) } };
}

/** Serialize theme cart writes and include the add-on before resolving the original cart event. */
export function cartAddonFetch(url, options, lineChange = null) {
  const config = configuration();
  if (!config) return fetch(url, options);
  let change = changeDetails(url, options, lineChange);
  const isManagedChange = () => change?.properties?.[PROPERTY] === 'true'
    && String(change.variantId) === String(config.variantId);
  const isAddonChange = isManagedChange();
  const revision = isAddonChange ? ++runtime.selectionRevision : null;
  if (isAddonChange) {
    state.selection = change.quantity > 0;
    state.error = '';
  }
  const queuedCart = state.cart;
  return enqueue(async () => {
    try {
      // Initial synchronization can finish while this edit waits in the queue.
      // Recover missing row metadata by its exact key, never by variant/position.
      if (change && !isManagedChange()) change = changeDetails(url, options, lineChange);
      if (change && state.cart !== queuedCart && state.cart
        && !state.cart.items.some((item) => item.key === currentKey(change.key))) throw new Error(STALE_CART);
      const outgoing = isManagedChange() ? lineChangeRequest(url, options, change, config, queuedCart) : { url, options };
      const response = await fetch(outgoing.url, outgoing.options);
      const original = await response.clone().json();
      if (!response.ok || original.status || original.errors) {
        // Shopify can partially add the available quantity and still return 422.
        // Reconcile the real cart without suppressing the original purchase error.
        if (change) {
          publish(await recover(new Error(original.description || original.message || String(original.errors || STALE_CART))));
        } else {
          try {
            state.cart = await renderSections(await reconcile(await request(''), config));
            publish(state.cart);
          } catch (error) { publish(await recover(error)); }
        }
        return response;
      }
      let cart;
      try {
        cart = await responseCart(original);
        if (isManagedChange() && change.quantity === 0) {
          // Keep both writes in the shared queue and do not reconcile between
          // them, so this page cannot re-add the product before opt-out persists.
          cart = await request('update', { attributes: { [PREFERENCE]: String(config.variantId) } });
        }
        if (isManagedChange() && change.quantity > 0) {
          const sent = JSON.parse(outgoing.options.body);
          const matches = cart.items.filter((item) => String(item.variant_id) === String(change.variantId)
            && sameProperties(item.properties, sent.properties));
          if (matches.length === 1 && matches[0].key !== sent.id) {
            runtime.keyChanges.set(sent.id, matches[0].key);
          }
        }
        cart = await reconcile(cart, config);
        cart = await renderSections(cart);
        state.cart = cart;
      } catch (error) {
        cart = await recover(error);
      }
      if (!cart) return response;
      const isAdd = /\/add(?:\.js)?(?:\?|$)/.test(String(url));
      // Let the product form reuse this same authoritative cart instead of fetching it again.
      const { sections, ...snapshot } = cart;
      return new Response(JSON.stringify(isAdd ? { ...original, sections, _velaCart: snapshot } : cart), {
        status: response.status, headers: { 'Content-Type': 'application/json' },
      });
    } catch (error) {
      // The cart row may already be optimistically removed. Restore Shopify's
      // actual cart if a line edit loses its response, then keep the error visible.
      if (change) publish(await recover(error));
      throw error;
    } finally {
      if (revision !== null && revision === runtime.selectionRevision) state.selection = null;
    }
  });
}

async function setSelected(selected) {
  const config = configuration();
  if (!config) return;
  const revision = ++runtime.selectionRevision;
  state.selection = selected;
  state.error = '';
  return enqueue(async () => {
    // Coalesce rapid clicks that have not started a network write yet.
    if (revision !== runtime.selectionRevision) return;
    try {
      const current = state.cart || await request('');
      const attributes = { [PREFERENCE]: selected ? '' : String(config.variantId) };
      const plan = addonPlan({ ...current, attributes: { ...current.attributes, ...attributes } }, config);
      // Persist the choice and remove/normalize existing managed lines together.
      const cart = await request('update', { attributes, updates: plan.updates });
      state.cart = await renderSections(await reconcile(cart, config));
    } catch (error) {
      await recover(error);
    }
    if (revision === runtime.selectionRevision) {
      state.selection = null;
      publish(state.cart);
    }
  });
}

function synchronize() {
  const config = configuration();
  if (!config) return Promise.resolve();
  return enqueue(async () => {
    try {
      const cart = await request('');
      const plan = addonPlan(cart, config);
      const changed = plan.add || plan.clearPreference || Object.keys(plan.updates).length;
      state.cart = await reconcile(cart, config);
      if (changed) publish(await renderSections(state.cart));
    } catch (error) {
      await recover(error);
    }
  });
}

class VelaCartAddon extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.onChange);
    document.addEventListener('vela:cart-addon-state', this.update);
    this.update();
  }
  disconnectedCallback() {
    this.removeEventListener('change', this.onChange);
    document.removeEventListener('vela:cart-addon-state', this.update);
  }
  onChange = (event) => {
    if (event.target.matches('[data-addon-checkbox]')) void setSelected(event.target.checked);
  };
  update = () => {
    const checkbox = this.querySelector('[data-addon-checkbox]');
    const message = this.querySelector('[data-addon-error]');
    const config = configuration();
    if (!checkbox || !config) return;
    checkbox.disabled = !config.available;
    this.setAttribute('aria-busy', String(state.busy));
    // Keep the preselected / just-clicked state while a cart write is pending.
    // Once it settles, reflect Shopify's actual line items, including failures.
    if (state.selection !== null) {
      checkbox.checked = state.selection;
    } else if (state.cart && !state.busy) {
      checkbox.checked = state.cart.items.some((item) =>
        item.properties?.[PROPERTY] === 'true' && String(item.variant_id) === String(config.variantId));
    }
    if (message) {
      message.textContent = state.error;
      message.hidden = !state.error || state.selection !== null;
    }
  };
}

if (!customElements.get('vela-cart-addon')) customElements.define('vela-cart-addon', VelaCartAddon);

const historyNavigation = window.performance?.getEntriesByType?.('navigation')?.[0]?.type === 'back_forward';
if (!runtime.lifecycleInitialized) {
  runtime.lifecycleInitialized = true;
  for (const type of new Set([
    StandardEvents.cartLinesUpdate, StandardEvents.cartDiscountUpdate,
    StandardEvents.cartNoteUpdate, StandardEvents.cartAttributesUpdate,
  ].filter(Boolean))) {
    document.addEventListener(type, (event) => {
      if (runtime.publishedEvents.has(event) || !event.promise) return;
      runtime.cartRevision += 1;
      const missedIconEvent = runtime.iconRecoveryScheduled && !customElements.get('cart-icon');
      const settled = Promise.resolve(event.promise).then(({ cart, detail }) => {
        // Match the cart icon's own lines-update contract. A later note or
        // discount event must not suppress the result of an earlier native add.
        if (type !== StandardEvents.cartLinesUpdate) return;
        const count = cart?.totalQuantity ?? cart?.item_count ?? detail?.itemCount;
        if (Number.isSafeInteger(count) && count >= 0) {
          runtime.latestCartCount = count;
          // An icon registered during this request missed the original event.
          if (missedIconEvent && customElements.get('cart-icon')) renderRecoveredIconCount();
        }
      }).catch(() => {}).finally(() => { runtime.cartEvents.delete(settled); });
      runtime.cartEvents.add(settled);
    });
  }
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) void restoreCartView();
  });
  if (historyNavigation) void restoreCartView();
}

if (configuration() && !runtime.initialized) {
  runtime.initialized = true;
  if (!historyNavigation) initialize();
  document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
    event.promise?.then(({ detail }) => {
      if (['product-form-component', 'cart-items-component', 'vela-cart-addon'].includes(detail?.source)) return;
      void synchronize();
    }).catch(() => {});
  });
  // Accelerated checkout controls can also be activated from the keyboard.
  document.addEventListener('click', (event) => {
    if (!runtime.pending || !event.target.closest?.('.additional-checkout-buttons')) return;
    event.preventDefault();
    event.stopImmediatePropagation();
  }, true);
}

if (!runtime.checkoutInitialized) {
  runtime.checkoutInitialized = true;
  // Quantities have already been persisted through change.js. Re-submitting
  // every visible quantity at checkout would overwrite changes from another tab.
  // Only the confirmed JS checkout omits them; native/no-JS cart forms keep them.
  document.addEventListener('formdata', (event) => {
    if (event.target !== runtime.checkoutForm) return;
    for (const name of [...event.formData.keys()]) {
      if (/^updates(?:\[|$)/.test(name)) event.formData.delete(name);
    }
  }, true);

  document.addEventListener('submit', async (event) => {
    if (!event.target.matches?.('form.cart-form') || !event.submitter?.matches('[name="checkout"]')) return;
    if (event.target === runtime.checkoutForm) return;
    event.preventDefault();
    if (runtime.checkoutWaiting) return;
    runtime.checkoutWaiting = true;
    const form = event.target;
    const submitter = event.submitter;
    const wasPending = runtime.pending > 0;
    try {
      // A trusted click can finish resolved awaits before the original submit
      // event has returned. Browsers ignore requestSubmit during that event.
      // Resume in the next task, then validate the latest quantities and queue.
      await new Promise((resolve) => setTimeout(resolve, 0));
      // Flush the 300ms input debounce as well as writes already in flight.
      const results = await Promise.all([...document.querySelectorAll('cart-items-component')]
        .map((component) => component.flushPendingQuantity?.() ?? true));
      do { await runtime.queue; } while (runtime.pending);
      if (results.includes(false) || (wasPending && state.error)) return;
      const currentForm = form.isConnected ? form : document.querySelector('form.cart-form');
      const currentSubmitter = submitter.isConnected ? submitter : document.querySelector('[name="checkout"]');
      if (currentForm && currentSubmitter && !currentSubmitter.disabled) {
        runtime.checkoutForm = currentForm;
        currentForm.requestSubmit(currentSubmitter);
      }
    } finally {
      runtime.checkoutForm = null;
      runtime.checkoutWaiting = false;
    }
  }, true);
}
